import React, { useEffect, useMemo, useState } from 'react';

import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { saveSmallGroupSession } from '@/data/smallGroups';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import CustomField from '@/components/CustomFields/CustomField';
import Callout from '@/components/General/Callout';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { supabase } from '@/data/client';
import useBibleStudies from '@/utils/hooks/useBibleStudies';

const EMPTY_DRAFT = {};

export default function AddSmallGroupModal() {
    const { id, smallGroupID, aiPrefill, typeID } = useLocalSearchParams()
    const storeKey = id || "new"
    
    const queryClient = useQueryClient()
    const router = useRouter()
    
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const smallGroupSessionDetails = useEditScreenStore(s => s.AddSmallGroupSessionModal?.[storeKey]) || EMPTY_DRAFT
    
    // Track if this form was pre-filled by AI
    const wasAIPrefilled = smallGroupSessionDetails?._aiPrefilled || aiPrefill === 'true'

    const { data: loggedInUser} = Query.people.loggedInUser()
    const { data: selectedSmallGroup } = Query.smallGroups.byId({id: smallGroupID, options: { enabled: !!smallGroupID }})
    const { data: smallGroupSessionToEdit } = Query.smallGroupSessions.byId({id: id, options: { enabled: !!id }});
    const smallGroupTypesQuery = Query.smallGroupTypes.all({});
    const { data: smallGroupTypes } = smallGroupTypesQuery;
    const { enabled: bibleStudiesEnabled } = useBibleStudies();

    // Flag-on: Bible Study sessions are reported through /biblestudies —
    // keep their types out of this ordinary type picker.
    const ordinaryTypesQueryResult = useMemo(() => bibleStudiesEnabled ? ({
        ...smallGroupTypesQuery,
        data: (smallGroupTypes || []).filter(t => t.purpose !== 'BIBLE_STUDY'),
    }) : undefined, [bibleStudiesEnabled, smallGroupTypes, smallGroupTypesQuery]);
    
    // Resolve the active type: explicit typeID param > small group's type > user-selected type (stored in edit screen) > null
    const typeFromParam = smallGroupTypes?.find(t => t.id === typeID);
    const typeFromSmallGroup = smallGroupTypes?.find(t => t.id === selectedSmallGroup?.smallGroupTypeID);
    const typeFromSelection = smallGroupTypes?.find(t => t.id === smallGroupSessionDetails?.smallGroupTypeID);
    const selectedType = typeFromParam || typeFromSmallGroup || typeFromSelection || null;
    const activeTypeID = selectedType?.id || null;
    const typeName = selectedType?.name || 'Small Group';

    const roleID = selectedType?.roleID;
    const { data: smallGroupTypeSections } = Query.customFieldSections.all({module: "SmallGroupType", options: { enabled: !!selectedType?.id }});
    const autoSelectMembers = selectedType?.autoSelectMembersOnSessionReport !== false;

    // When on, the Attendees dropdown is filtered to only this small group's members.
    // Default on; user can toggle off to pick from all people.
    const [onlyMyGroupMembers, setOnlyMyGroupMembers] = useState(true);
    const activeSmallGroupID = smallGroupSessionDetails?.smallGroupID || smallGroupID;

    const smallGroupMembersQuery = useInfiniteQuery({
        queryKey: ['smallGroupMembers', activeSmallGroupID],
        queryFn: async () => {
            const { data, error } = await supabase.from('SmallGroupMembers')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('smallGroupID', activeSmallGroupID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!activeSmallGroupID && onlyMyGroupMembers,
    });

    // SheetSelector for type="people" expects an infinite-query shape: { pages: [{ data: [] }] }
    const attendeesQueryResult = useMemo(() => ({
        data: { pages: [{ data: smallGroupMembersQuery.data?.pages?.[0]?.data || [] }] },
        isFetching: smallGroupMembersQuery.isFetching,
        refetch: smallGroupMembersQuery.refetch,
    }), [smallGroupMembersQuery.data, smallGroupMembersQuery.isFetching, smallGroupMembersQuery.refetch]);

    const rolePeopleQuery = useInfiniteQuery({
        queryKey: ['peopleByRole', roleID],
        queryFn: async ({pageParam: _pageParam = 0}) => {
            const { data, error } = await supabase.from('RolePeople')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('roleID', roleID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!roleID
    });
    
    const saveSmallGroupSessionMutation = useMutation({
        mutationFn: saveSmallGroupSession,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            queryClient.invalidateQueries({queryKey: ['people']});
            removeEditScreenData("AddSmallGroupSessionModal", storeKey)
            redirectToDefault(router, "AddSmallGroupSessionModal")
            Toast.delayShow({type: 'success', text2: 'New Small Group Session has been created successfully!'})
        }
    })

    /*** Effects ***/
    useEffect(() => {
        if(selectedSmallGroup){
            setSmallGroupSessionDetails({
                ...smallGroupSessionDetails,
                smallGroupID: smallGroupID,
                SmallGroup: selectedSmallGroup,
                Leader: selectedSmallGroup?.Leader,
                leaderID: selectedSmallGroup?.Leader?.id,
                Attendees: autoSelectMembers ? selectedSmallGroup?.Students?.map(s => s.Person) : (smallGroupSessionDetails?.Attendees || []),
            })
        }
    }, [selectedSmallGroup, autoSelectMembers])

    useEffect(() => {
        if(!smallGroupID){
            setSmallGroupSessionDetails({
                Leader: loggedInUser,
                leaderID: loggedInUser?.id,
            })
        }
    }, [])

    useEffect(() => {
        if(smallGroupSessionToEdit){
            const mappedSession = {
                id: smallGroupSessionToEdit.id,
                sessionDate: smallGroupSessionToEdit.startTime,
                notes: smallGroupSessionToEdit.notes,
                Leader: smallGroupSessionToEdit.EventSpeakers?.[0]?.Person,
                leaderID: smallGroupSessionToEdit.EventSpeakers?.[0]?.personID,
                Attendees: smallGroupSessionToEdit.Students?.map(s => s.Person) || [],
                customFieldValues: smallGroupSessionToEdit.customFieldValues || {},
            };
            setSmallGroupSessionDetails(mappedSession)
        }
    }, [smallGroupSessionToEdit]);

    const setSmallGroupSessionDetails = (details) => {
        setEditScreenData("AddSmallGroupSessionModal", storeKey, details)
    }

    return (
        <ModalWrapper 
            loading={saveSmallGroupSessionMutation.isPending} 
            onConfirm={() => {
                if (!smallGroupSessionDetails.smallGroupID) {
                    Toast.show({ type: 'error', text2: 'Please select a Small Group' });
                    return;
                }

                if (!smallGroupSessionDetails.sessionDate) {
                    Toast.show({ type: 'error', text2: 'Please select a session date' });
                    return;
                }

                if (!smallGroupSessionDetails.leaderID) {
                    Toast.show({ type: 'error', text2: 'Please select a Leader for this session' });
                    return;
                }

                const missingRequiredFields = (selectedType?.CustomFields || []).filter(f => f.displayOn === 'session' && f.required && !f.isDeleted);
                const missingField = missingRequiredFields.find(f => {
                    const val = smallGroupSessionDetails.customFieldValues?.[f.id];
                    return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                });
                if(missingField){
                    Toast.show({type: 'error', text2: `Please fill out the required field: ${missingField.name}`})
                    return;
                }

                saveSmallGroupSessionMutation.mutate({details: {...smallGroupSessionDetails}})
            }} 
            title={id ? "Update a Session" : "Add a Session"}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                {/* Show callout if form was pre-filled by AI */}
                {wasAIPrefilled && (
                    <Callout 
                        type="info" 
                        message="This form was pre-filled by AI. Please review the information before submitting."
                    />
                )}
                
                {/* If no typeID/smallGroupID is known, let the user pick a Small Group Type first */}
                {!activeTypeID && (
                    <SheetSelector
                        type="smallGroupTypes"
                        queryResult={ordinaryTypesQueryResult}
                        title="Small Group Type"
                        helpText="Select the type of Small Group to filter the list."
                        fromScreen={`smallgroups/sessions/create`}
                        fromStoreKey={storeKey}
                        value={selectedType ? { id: selectedType.id, name: selectedType.name } : null}
                        onChange={(selected_item) => {
                            setSmallGroupSessionDetails({
                                ...smallGroupSessionDetails,
                                smallGroupTypeID: selected_item?.id,
                                SmallGroup: undefined,
                                smallGroupID: undefined,
                            })
                        }}
                    />
                )}

                <SheetSelector
                    required
                    type="smallGroups"
                    smallGroupTypeID={activeTypeID}
                    title={activeTypeID ? typeName : "Small Group"}
                    addScreenName={loggedInUser?.can?.("Create Small Groups") ? "smallgroups/create" : null}
                    fromScreen={`smallgroups/sessions/create`}
                    fromStoreKey={storeKey}
                    value={smallGroupSessionDetails?.SmallGroup}
                    onChange={(selected_item) => {
                        setSmallGroupSessionDetails({
                            ...smallGroupSessionDetails,
                            smallGroupID: selected_item?.id,
                            SmallGroup: selected_item,
                            smallGroupTypeID: selected_item?.smallGroupTypeID || activeTypeID,
                        })
                    }}
                />

                <InputSection title="Leader" description="Select the leader for this session.">
                    <SheetSelector
                        required
                        type="people"
                        queryResult={roleID ? rolePeopleQuery : undefined}
                        title="Leader"
                        fromScreen={`smallgroups/sessions/create`}
                        fromStoreKey={storeKey}
                        value={smallGroupSessionDetails?.Leader}
                        onChange={(selected_item) => {
                            setSmallGroupSessionDetails({
                                ...smallGroupSessionDetails,
                                leaderID: selected_item?.id,
                                Leader: selected_item
                            })
                        }}
                    />
                </InputSection>

                <DatePickerInput
                    required
                    title="Date of Session"
                    mode='date'
                    value={smallGroupSessionDetails.sessionDate}
                    onUpdate={(new_value) => {
                        setSmallGroupSessionDetails({
                            ...smallGroupSessionDetails,
                            sessionDate: new_value
                        })
                    }}
                />
                
                <InputSection title="Attendees" description="Select who attended this session.">
                    {!!activeSmallGroupID && (
                        <ToggleRow
                            label="Only show my group members"
                            description="Filter the Attendees list to only members of this small group. Turn off to pick from all people."
                            value={onlyMyGroupMembers}
                            onValueChange={setOnlyMyGroupMembers}
                        />
                    )}

                    <SheetSelector multiple noDefaultTruncated type="people" addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={`smallgroups/sessions/create`} fromStoreKey={storeKey} queryResult={onlyMyGroupMembers && activeSmallGroupID ? attendeesQueryResult : undefined} value={smallGroupSessionDetails.Attendees || []} onChange={(selected_items) => {
                        setSmallGroupSessionDetails({
                            ...smallGroupSessionDetails,
                            Attendees: selected_items
                        })
                    }}/>
                </InputSection>

                {(smallGroupTypeSections || []).map(section => {
                    const sectionFields = (selectedType?.CustomFields || []).filter(f => f.sectionID === section.id && f.displayOn === 'session');
                    if (sectionFields.length === 0) return null;
                    return (
                        <InputSection key={section.id} title={section.name}>
                            {sectionFields.map(field => (
                                <CustomField
                                    key={field.id}
                                    fieldType={field.type}
                                    required={field.required}
                                    onUpdate={(val) => {
                                        const oldVals = smallGroupSessionDetails.customFieldValues || {};
                                        setSmallGroupSessionDetails({ customFieldValues: { ...oldVals, [field.id]: val } });
                                    }}
                                    options={{
                                        title: field.name,
                                        value: smallGroupSessionDetails.customFieldValues?.[field.id],
                                        options: field.options,
                                    }}
                                />
                            ))}
                        </InputSection>
                    );
                })}

                <Input
                    title="Notes"
                    multiline
                    value={smallGroupSessionDetails.notes}
                    onUpdate={(new_value) => {
                        setSmallGroupSessionDetails({
                            ...smallGroupSessionDetails,
                            notes: new_value
                        })
                    }}
                />
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}

